import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  retryCount: number;
}

export class EnhancedErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log error for monitoring
    console.error('Enhanced Error Boundary caught an error:', error, errorInfo);
    
    // Report to external service (if configured)
    this.reportError(error, errorInfo);
    
    // Call custom error handler
    this.props.onError?.(error, errorInfo);
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.state.retryCount
    };

    // In a real app, send to monitoring service
    console.warn('Error report:', errorReport);
  };

  private handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReportBug = () => {
    const errorDetails = {
      message: this.state.error?.message,
      stack: this.state.error?.stack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString()
    };
    
    const subject = encodeURIComponent(`Bug Report - ${this.state.errorId}`);
    const body = encodeURIComponent(
      `Error Details:\n\nError ID: ${errorDetails.errorId}\nTimestamp: ${errorDetails.timestamp}\nMessage: ${errorDetails.message}\n\nStack Trace:\n${errorDetails.stack}\n\nSteps to reproduce:\n1. \n2. \n3. \n\nExpected behavior:\n\n\nActual behavior:\n`
    );
    
    window.open(`mailto:support@csoagent.com?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isNetworkError = this.state.error?.message?.includes('network') || 
                           this.state.error?.message?.includes('fetch');
      const isUserError = this.state.error?.message?.includes('permission') ||
                         this.state.error?.message?.includes('unauthorized');

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-2xl w-full">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-destructive/10 p-3">
                  <AlertTriangle className="h-8 w-8 text-destructive" />
                </div>
              </div>
              <CardTitle className="text-2xl">
                {isNetworkError ? 'Connection Problem' : 
                 isUserError ? 'Access Denied' : 
                 'Something went wrong'}
              </CardTitle>
              <CardDescription className="text-base">
                {isNetworkError ? 
                  'We\'re having trouble connecting to our servers. Please check your internet connection and try again.' :
                 isUserError ?
                  'You don\'t have permission to access this resource. Please check your account or contact support.' :
                  'An unexpected error occurred while loading this page. Our team has been notified.'}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={this.handleRetry} className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
                <Button variant="outline" onClick={this.handleGoHome} className="flex-1">
                  <Home className="h-4 w-4 mr-2" />
                  Go Home
                </Button>
                <Button variant="outline" onClick={this.handleReportBug}>
                  <Bug className="h-4 w-4 mr-2" />
                  Report Bug
                </Button>
              </div>

              <div className="text-center">
                <Badge variant="secondary" className="text-xs">
                  Error ID: {this.state.errorId}
                </Badge>
                {this.state.retryCount > 0 && (
                  <Badge variant="outline" className="text-xs ml-2">
                    Retry attempts: {this.state.retryCount}
                  </Badge>
                )}
              </div>

              {this.props.showDetails && this.state.error && (
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full">
                      Show Technical Details
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4">
                    <div className="bg-muted p-4 rounded-lg text-sm">
                      <div className="font-medium mb-2">Error Message:</div>
                      <code className="text-xs bg-background p-2 rounded block">
                        {this.state.error.message}
                      </code>
                    </div>
                    
                    {this.state.error.stack && (
                      <div className="bg-muted p-4 rounded-lg text-sm">
                        <div className="font-medium mb-2">Stack Trace:</div>
                        <code className="text-xs bg-background p-2 rounded block max-h-40 overflow-y-auto whitespace-pre-wrap">
                          {this.state.error.stack}
                        </code>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              )}

              <div className="text-xs text-muted-foreground text-center">
                If this problem persists, please contact our support team with the error ID above.
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}